import React, {useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';

import {getFormTableSwitcherStatus} from 'notebook/structured-tables/action-functions/form-table-switcher/FormTableSwitcherStateActions';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import useLocalStorageColumnWidthStore from 'notebook/structured-tables/hooks/useLocalStorageColumnWidthStore';
import useStructuredTableColumnWidths from 'notebook/structured-tables/hooks/useStructuredTableColumnWidths';
import {
  StructuredTable,
  StructuredTableColumn,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableOrFormOutsideNotebook, {
  TableOrFormRenderOptions,
  TableOrFormRenderOptionsType,
} from 'notebook/structured-tables/StructuredTableOrFormOutsideNotebook';
import doesEvaluatedTableHaveAnyErrors from 'notebook/tables/errors/doesEvaluatedTableHaveAnyErrors';
import {MethodComponent} from 'procedures/ProcedureDomainTypes';
import MethodSidepanelContext from 'procedures/processes/MethodSidepanelContext';
import {BenchlingState} from 'redux-stores/reduceApp';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';

// One localStorage record per config-table kind, so a user's column widths carry across every
// procedure and method version (config-table column provenanceUuids are stable, shared constants).
const COLUMN_WIDTHS_STORAGE_KEY_PREFIX = 'procedure-config-column-widths:';

interface ConnectedProps {
  renderType: TableOrFormRenderOptionsType;
}

interface ProcedureConfigTableProps<
  T extends MethodComponent,
  A extends GenericStructuredTableAdapter<TTableMetadata, TRowMetadata>,
  TTableMetadata,
  TRowMetadata,
> {
  onChangeDetails: (newDetails: Array<T>) => void;
  onChangeErrorState: (anyErrors: boolean) => void;
  tableCid: string;
  tableAdapter: A;
  rowToDetail: (row: StructuredTableRow<TRowMetadata>, columns: Array<StructuredTableColumn>) => T;
  locked?: boolean;
  table: StructuredTable<TTableMetadata, TRowMetadata>;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * Presents a way to edit multiple values of the same type (e.g., "all parameters" or "all equipments")
 * using either a form or a table.
 */
function UnconnectedProcedureConfigTable<
  T extends MethodComponent,
  A extends GenericStructuredTableAdapter<TTableMetadata, TRowMetadata>,
  TTableMetadata,
  TRowMetadata,
>(props: ProcedureConfigTableProps<T, A, TTableMetadata, TRowMetadata> & ConnectedProps): JSX.Element {
  const {onChangeDetails, rowToDetail, locked, onChangeErrorState} = props;
  const [errorState, setErrorState] = useState<boolean>(false);

  const updateTable = useCallback(
    (newTable: StructuredTable<TTableMetadata, TRowMetadata>) => {
      const newDetails = newTable.rows.map((row) => rowToDetail(row, newTable.columns));
      onChangeDetails(newDetails);
    },
    [onChangeDetails, rowToDetail]
  );

  useEffect(() => {
    onChangeErrorState?.(errorState);
  }, [errorState, onChangeErrorState]);

  // Column resizing is enabled only in the Processes-on-Workflows editor, whose presence is signalled
  // by a non-null saveImmediately; the legacy editor hides the handles and applies no stored widths.
  const {saveImmediately} = MethodSidepanelContext.useContext();
  const isProcessesOnWorkflows = saveImmediately != null;
  const columnWidthStore = useLocalStorageColumnWidthStore(
    `${COLUMN_WIDTHS_STORAGE_KEY_PREFIX}${props.table.kind}`
  );
  const {structuredTable: resizableStructuredTable, updateHandler: resizableUpdateHandler} =
    useStructuredTableColumnWidths({
      structuredTable: props.table,
      updateHandler: updateTable,
      store: columnWidthStore,
    });
  // In the legacy editor, bypass the width hook entirely so every update (including a width-only one)
  // reaches updateTable unchanged. Routing through it there would silently swallow width-only updates.
  const structuredTable = isProcessesOnWorkflows ? resizableStructuredTable : props.table;
  const updateHandler = isProcessesOnWorkflows ? resizableUpdateHandler : updateTable;

  return (
    <div>
      <StructuredTableOrFormOutsideNotebook
        shouldShowAs={props.renderType}
        structuredTable={structuredTable}
        cid={props.tableCid}
        updateHandler={updateHandler}
        enableColumnResizing={isProcessesOnWorkflows}
        onComputedTableChanged={(computedTable) => {
          setErrorState(doesEvaluatedTableHaveAnyErrors(computedTable));
        }}
        includeUnsubmittedStatus={true}
        hideColumnHeaderIcons={true}
        hasPrimarySamples={false}
        isCollapsible={true}
        locked={locked}
        canRename={false}
        dataTestAttrs={props.dataTestAttrs}
      />
    </div>
  );
}

function mapStateToProps(state: BenchlingState, props: {tableCid: string}): ConnectedProps {
  return {
    renderType: getFormTableSwitcherStatus(state, props.tableCid) ?? TableOrFormRenderOptions.TABLE,
  };
}

const ProcedureConfigTable = connect(mapStateToProps)(UnconnectedProcedureConfigTable);
export default ProcedureConfigTable;
